/**
 * reconcileTranslatedCaptionRows.ts
 *
 * Reconcile for PHRASE-derived caption rows — the translated path.
 *
 * `KaraokeTrackManager`'s token walk exists for TOKEN-derived rows: it
 * re-partitions the transcript into clips by character-length heuristics
 * (`segmentTokens`), assuming one token belongs to exactly one clip. Translated
 * rows break both halves of that assumption:
 *
 *   - Their text comes from `TranslationPhrase`s baked at produce time, not from
 *     the tokens, so re-partitioning cannot reproduce it — which is why
 *     `splitClipsIntoSegments` already refuses to touch them.
 *   - A synthetic line break gives BOTH halves the SAME `transcriptTokens`
 *     array, so a `Map<tokenId, clip>` cannot even represent them. Last write
 *     won, the right half claimed every token, and the left ROW VANISHED on the
 *     next reconcile — which runs on every command's `recalculateTimeline`, so
 *     toggling an end card was enough to eat a caption line.
 *
 * So reconcile's only legitimate business with these rows is token MEMBERSHIP:
 * which words died. Row count, boundaries and text belong to the producer and to
 * the split/merge commands. This module filters; it never re-partitions.
 *
 * Pure — no editor, no MobX, no IO.
 */

import { KaraokeClip } from '../models/karaoke/KaraokeClip';
import { keepPhraseSoftBreaks } from './captionSoftBreaks';
import {
  joinCaptionRowTexts,
  phraseTimingFromBucket,
} from './karaokeSegmentation';

import type { CaptionLineBreak } from '../models/karaoke/CaptionLineBreak';
import type {
  TranscriptToken,
  TranscriptTokenClipSegmentPositionProps,
} from '../types';

/** One `translatedPhrases` entry's shape (baked per-phrase caption timing). */
type PhraseEntry = NonNullable<KaraokeClip['translatedPhrases']>[number];

/** Timeline start of a token, or null for a non-text token that carries none. */
function timelineTsOf(token: TranscriptToken): number | null {
  return token.type === 'text' ? (token.ts ?? null) : null;
}

/**
 * Whether two tokens still resolve to the same place in the transcript.
 *
 * `clip_segment_id` + `clip_segment_position` is the address every
 * segment-addressed transcript command looks a token up by, and
 * `ClipTranscriptSegment` ids are regenerated on every rebuild — so a token can
 * keep its id and its timing while its address dies under it. Attached at
 * runtime by `getActiveTranscriptElements`; not on the `TranscriptToken` union,
 * hence the narrow view.
 */
function sameSegmentAddress(a: TranscriptToken, b: TranscriptToken): boolean {
  const x = a as Partial<TranscriptTokenClipSegmentPositionProps>;
  const y = b as Partial<TranscriptTokenClipSegmentPositionProps>;
  return (
    x.clip_segment_id === y.clip_segment_id &&
    x.clip_segment_position === y.clip_segment_position
  );
}

/** Timeline end of a token, or null for a non-text token that carries none. */
function timelineEndOf(token: TranscriptToken): number | null {
  return token.type === 'text' ? (token.end_ts ?? null) : null;
}

/**
 * Re-derive a phrase entry's `[ts, endTs]` from the row's CURRENT tokens.
 *
 * `translatedPhrases[].ts/endTs` are timeline-absolute times baked at produce
 * time (`[min token.ts, max token.end_ts]` over the phrase's source words). They
 * go stale the moment an upstream edit re-times those words — a head trim or a
 * removal earlier in the video shifts every later token's `ts` without changing
 * THIS row's membership. Both consumers read the baked values directly: the
 * canvas highlight derives its per-word `transcript_source` timing from them
 * (and, when the stale span collapses, emits zero-duration units that never
 * advance — the "captions show but don't highlight" symptom), and a word click
 * seeks to `ts + proportion * (endTs - ts)`. So reconcile must retime them.
 *
 * The entry is matched to its words by `originalIndices → initial_index`. When
 * that join can't resolve (no indices, or a reused clip renumbered to `-1`) the
 * baked value is kept — the same restraint the rest of this module applies. The
 * SAME object is returned when nothing changed, so callers can detect a no-op by
 * reference.
 */
function retimePhrase(
  phrase: PhraseEntry,
  keptTokens: readonly TranscriptToken[],
): PhraseEntry {
  const indices = phrase.originalIndices;
  if (!indices || indices.length === 0) return phrase;
  const wanted = new Set(indices);
  const bucket = keptTokens.filter(
    token => token.type === 'text' && wanted.has(token.initial_index),
  );
  const timing = phraseTimingFromBucket(bucket);
  if (!timing) return phrase;
  if (timing.ts === phrase.ts && timing.endTs === phrase.endTs) return phrase;
  return { ...phrase, ts: timing.ts, endTs: timing.endTs };
}

export interface TranslatedRowsReconcileResult {
  /**
   * Surviving phrase-derived rows in their original relative order. Instances
   * are REUSED untouched when nothing died — the safest possible outcome, since
   * a rebuilt clip can only ever lose fields it forgot to copy.
   */
  rows: KaraokeClip[];
  /**
   * Token ids the surviving rows still hold. The token walk must skip these:
   * they are already accounted for, and a split pair's shared tokens would
   * otherwise collapse the pair back into one row.
   */
  ownedTokenIds: Set<string>;
  /**
   * Break records whose phrase lost every source word. Retiring them keeps the
   * track consistent — a record pointing at a phrase that no longer exists can
   * never be matched or undone, it just accumulates.
   */
  retiredBreakIds: Set<string>;
}

/** A row is phrase-derived exactly when the producer baked translated text on it. */
export function isPhraseDerivedRow(clip: KaraokeClip): boolean {
  return clip.translatedText != null;
}

/**
 * Whether this row's phrase indices can be trusted to decide survival at all.
 *
 * Phrase identity joins `TranslationPhrase.originalIndices` to
 * `TranscriptToken.initial_index`. That join is NOT always available: a reused or
 * reordered clip has its `initial_index` renumbered — often to `-1` — while the
 * phrases keep the indices they were translated against. Then nothing matches,
 * every phrase looks dead, and judging survival by the join wipes a perfectly
 * good translation. (Before this guard that produced the reported symptom
 * exactly: rows dropped, then rebuilt by the token walk as one merged
 * source-language row.)
 *
 * So the join is only usable when at least one of the row's phrases references an
 * index the row's OWN tokens actually carry. Otherwise we cannot prove anything
 * and must keep the text — the same rule already applied to entries with no
 * `originalIndices`.
 */
function canJudgeSurvivalByIndex(
  clip: KaraokeClip,
  ownIndicesBefore: ReadonlySet<number>,
): boolean {
  return Boolean(
    clip.translatedPhrases?.some(phrase =>
      phrase.originalIndices?.some(index => ownIndicesBefore.has(index)),
    ),
  );
}

/**
 * Which of `clip`'s phrase entries still have at least one surviving source
 * word. An entry with no `originalIndices` cannot be checked, so it is kept
 * rather than dropping text we cannot prove is stale.
 *
 * Survival is judged against the ROW'S OWN surviving words, not the whole
 * transcript: a row only loses a phrase when the words behind it left THIS row.
 */
function survivingPhrases(
  clip: KaraokeClip,
  ownIndicesAfter: ReadonlySet<number>,
  indexJoinUsable: boolean,
) {
  if (!indexJoinUsable) return clip.translatedPhrases;
  return clip.translatedPhrases?.filter(
    phrase =>
      !phrase.originalIndices?.length ||
      phrase.originalIndices.some(index => ownIndicesAfter.has(index)),
  );
}

/** Survival inputs for one row, all derived from its OWN tokens. */
function rowSurvival(clip: KaraokeClip, activeTokenIds: ReadonlySet<string>) {
  const ownIndicesBefore = sourceIndicesOf(clip.transcriptTokens);
  const ownIndicesAfter = sourceIndicesOf(
    clip.transcriptTokens.filter(token => activeTokenIds.has(token.id)),
  );
  const indexJoinUsable = canJudgeSurvivalByIndex(clip, ownIndicesBefore);
  return {
    indexJoinUsable,
    kept: survivingPhrases(clip, ownIndicesAfter, indexJoinUsable),
  };
}

/** Source-word indices still present among `tokens`. */
function sourceIndicesOf(tokens: readonly TranscriptToken[]): Set<number> {
  const indices = new Set<number>();
  for (const token of tokens) {
    if (token.type !== 'text') continue;
    indices.add(token.initial_index);
  }
  return indices;
}

export function reconcileTranslatedCaptionRows(
  clips: readonly KaraokeClip[],
  activeTokens: readonly TranscriptToken[],
  syntheticBreaks: readonly CaptionLineBreak[] = [],
): TranslatedRowsReconcileResult {
  const activeTokenIds = new Set(activeTokens.map(token => token.id));
  const activeSourceIndices = sourceIndicesOf(activeTokens);

  const rows: KaraokeClip[] = [];
  const ownedTokenIds = new Set<string>();

  // Rows that came from a synthetic break SHARE one `transcriptTokens` array,
  // and `MergeAdjacentCaptionClipsCommand` identifies such a pair by REFERENCE
  // equality of that array (`sharedTokens`). So a group's tokens are resolved
  // once and the one array handed to every row in it — rebuild them separately
  // and merging silently stops fusing the halves.
  const tokensForGroup = new Map<
    readonly TranscriptToken[],
    TranscriptToken[]
  >();
  const resolveGroupTokens = (
    group: readonly TranscriptToken[],
    coveredIndices: ReadonlySet<number>,
  ): TranscriptToken[] => {
    const cached = tokensForGroup.get(group);
    if (cached) return cached;
    const originalIds = new Set(group.map(token => token.id));
    // Taken from the ACTIVE list, so a restored word lands back in source order
    // rather than being appended. A word is claimed when the row already held it
    // or when a surviving phrase covers its source index — that phrase's
    // translation was written for it, so the row is where it belongs. Anything
    // else stays the token walk's business.
    const resolved = activeTokens.filter(
      token =>
        originalIds.has(token.id) ||
        (token.type === 'text' && coveredIndices.has(token.initial_index)),
    );
    tokensForGroup.set(group, resolved);
    return resolved;
  };

  // Coverage is a property of the GROUP: either half of a split may be the one
  // whose phrases cover a restored word, and both must claim it.
  const coveredByGroup = new Map<readonly TranscriptToken[], Set<number>>();
  for (const clip of clips) {
    if (!isPhraseDerivedRow(clip)) continue;
    const { kept } = rowSurvival(clip, activeTokenIds);
    if (kept && kept.length === 0) continue; // this row is going away
    let covered = coveredByGroup.get(clip.transcriptTokens);
    if (!covered) {
      covered = new Set<number>();
      coveredByGroup.set(clip.transcriptTokens, covered);
    }
    for (const phrase of kept ?? []) {
      for (const index of phrase.originalIndices ?? []) covered.add(index);
    }
    for (const index of clip.phraseOriginalIndices ?? []) covered.add(index);
  }

  for (const clip of clips) {
    if (!isPhraseDerivedRow(clip)) continue;

    const { kept, indexJoinUsable } = rowSurvival(clip, activeTokenIds);
    // `undefined` = older data with no per-phrase breakdown; the row is one unit
    // and survives as long as it still has tokens.
    if (kept && kept.length === 0) continue;

    const keptTokens = resolveGroupTokens(
      clip.transcriptTokens,
      coveredByGroup.get(clip.transcriptTokens) ?? new Set<number>(),
    );
    if (keptTokens.length === 0) continue; // every word behind the row is gone

    // Retime the surviving phrases against the CURRENT tokens (see
    // `retimePhrase`). `retimedPhrases` is the authoritative timing for the
    // rebuilt row; comparing it to the baked entries is also how we detect that a
    // reused instance would ship stale highlight/seek timing.
    //
    // Skip a synthetic-break / merged row (`siblingGroupId`): its phrase timing is
    // a TEXT-proportional slice deliberately not aligned to token boundaries (the
    // split command owns it), so token min/max would move the line break. Those
    // rows still get fresh token objects below — so the clip's own position tracks
    // the video — they just keep their baked per-phrase split timing.
    const retimedPhrases = kept?.map(phrase =>
      clip.siblingGroupId ? phrase : retimePhrase(phrase, keptTokens),
    );

    // `keptTokens` are freshly resolved from the active transcript, so their
    // timestamps track the current video. A row may keep the same token IDS yet
    // need a rebuild because a head trim / upstream removal shifted their `ts` —
    // reusing the old instance there is exactly bug PLAT-10862 (stale clip
    // position + stale phrase timing). So the reuse gate compares timing, not
    // just identity.
    const tokensUnchanged =
      keptTokens.length === clip.transcriptTokens.length &&
      keptTokens.every((token, index) => {
        const previous = clip.transcriptTokens[index];
        return (
          previous != null &&
          token.id === previous.id &&
          sameSegmentAddress(token, previous) &&
          timelineTsOf(token) === timelineTsOf(previous) &&
          timelineEndOf(token) === timelineEndOf(previous)
        );
      });
    const phrasesUnchanged =
      !retimedPhrases ||
      (retimedPhrases.length === (clip.translatedPhrases?.length ?? 0) &&
        // `retimePhrase` returns the SAME object when a phrase's timing is
        // unchanged, so reference equality means nothing needs rebaking.
        retimedPhrases.every(
          (phrase, index) => phrase === clip.translatedPhrases?.[index],
        ));

    if (tokensUnchanged && phrasesUnchanged) {
      // Nothing died AND nothing moved: reuse the instance rather than rebuilding
      // it. This is the end-card case, and the whole point of not re-partitioning.
      rows.push(clip);
      for (const token of clip.transcriptTokens) ownedTokenIds.add(token.id);
      continue;
    }

    const keptTokenIds = new Set(keptTokens.map(token => token.id));
    const survivingOwnIndices = indexJoinUsable
      ? clip.phraseOriginalIndices?.filter(index =>
          activeSourceIndices.has(index),
        )
      : clip.phraseOriginalIndices;
    rows.push(
      new KaraokeClip({
        id: clip.id,
        transcriptTokens: keptTokens,
        karaokeConfig: clip.karaokeConfig,
        track: clip.track,
        hiddenTranscriptTokenIds: clip.hiddenTranscriptTokenIds.filter(id =>
          keptTokenIds.has(id),
        ),
        ignoredByFiltersIds: clip.ignoredByFiltersIds.filter(id =>
          keptTokenIds.has(id),
        ),
        // Offsets carve this row out of a shared token range, so they only mean
        // anything while the row's edge words are still there.
        startSecondsOffset:
          keptTokens[0]?.id === clip.transcriptTokens[0]?.id
            ? clip.startSecondsOffset
            : undefined,
        endSecondsOffset:
          keptTokens[keptTokens.length - 1]?.id ===
          clip.transcriptTokens[clip.transcriptTokens.length - 1]?.id
            ? clip.endSecondsOffset
            : undefined,
        // Same CJK-aware join every caption-row composer uses (PLAT-11468).
        translatedText: retimedPhrases
          ? joinCaptionRowTexts(retimedPhrases.map(phrase => phrase.text))
          : clip.translatedText,
        // Retimed against the current tokens (never the baked timing) so the
        // canvas highlight and word-click seek stay aligned with the video.
        translatedPhrases: retimedPhrases?.map(phrase => ({ ...phrase })),
        // Normalised back to undefined when empty — the synthetic-break commands
        // treat "no phrase identity" as absent, not as an empty match set.
        phraseOriginalIndices: survivingOwnIndices?.length
          ? survivingOwnIndices
          : undefined,
        siblingGroupId: clip.siblingGroupId,
        // Positions index `translatedPhrases`, so they follow the kept entries.
        softBreakPhrasePositions: kept
          ? keepPhraseSoftBreaks(
              clip.softBreakPhrasePositions,
              clip.translatedPhrases ?? [],
              kept,
            )
          : clip.softBreakPhrasePositions,
      }),
    );
    for (const token of keptTokens) ownedTokenIds.add(token.id);
  }

  // Retiring a record is only safe when the phrase behind it is PROVABLY gone,
  // and "words deleted" vs "indices renumbered" look identical through the index
  // join. So:
  //   - Every phrase-derived row died (proven by token id, no index needed) ⇒
  //     every record is orphaned. This is the transcript-replaced case.
  //   - Otherwise retire only what the index join can prove, and only when that
  //     join demonstrably works on this track. Under a renumbered transcript it
  //     proves nothing, and guessing would throw away the user's line breaks —
  //     a few dead records are harmless by comparison, since nothing replays them.
  const hadPhraseDerivedRows = clips.some(isPhraseDerivedRow);
  const allRowsDied = hadPhraseDerivedRows && rows.length === 0;
  const indexJoinWorksHere = clips.some(
    clip =>
      isPhraseDerivedRow(clip) &&
      clip.translatedPhrases?.some(phrase =>
        phrase.originalIndices?.some(index => activeSourceIndices.has(index)),
      ),
  );
  const retiredBreakIds = new Set(
    allRowsDied
      ? syntheticBreaks.map(record => record.id)
      : indexJoinWorksHere
        ? syntheticBreaks
            .filter(
              record =>
                !record.phraseOriginalIndices.some(index =>
                  activeSourceIndices.has(index),
                ),
            )
            .map(record => record.id)
        : [],
  );

  return { rows, ownedTokenIds, retiredBreakIds };
}
